/* eslint-disable complexity */
import { i18n } from 'next-i18next'
import type { TFunction } from 'next-i18next'
import { getProjectUnit } from './project-helpers'
import { getContributionUnitInUserLanguage } from '../json'
import { getStringWithOutColomns } from './string-builder.helper'
import { downloadCsv, getCsvFromObjectArray } from './csv-download'
import { Currency, Sale, SaleStatusType, BigSale } from '../models'
import { getActualDateFormated, getTransformedDate } from './date-helpers'
import { getFormatedPrice } from './currency.helpers'

interface SaleStatusParams {
  hasCFPA: boolean
  isCFPAValidated: boolean
  hasCertificate: boolean
  isCertificateValidated: boolean
}

export const getCarbonForwardSaleStatus = ({
  hasCFPA,
  isCFPAValidated,
  hasCertificate,
  isCertificateValidated
}: SaleStatusParams): SaleStatusType => {

  if (!hasCFPA) {
    return 'AWAITING_DOCS'
  } else if (hasCFPA && !isCFPAValidated) {
    return 'AWAITING_VALIDATION'
  } else if (hasCFPA && isCFPAValidated && !hasCertificate) {
    return 'CREDITS_COMMITTED'
  } else if (hasCFPA && isCFPAValidated && hasCertificate && !isCertificateValidated) {
    return 'CREDITS_IN_DELIVERY'
  } else if (hasCFPA && isCFPAValidated && hasCertificate && isCertificateValidated) {
    return 'COMPLETED'
  }
  return 'IN_REVIEW'
}

export const getSaleStatus = (sale: Sale): SaleStatusType => {
  if (sale.projectKind === 'carbon_forward') return getCarbonForwardSaleStatus({
    hasCFPA: sale.carbonForwardAgreementFile !== '',
    isCFPAValidated: sale.carbonForwardAgreementFileIsValid,
    hasCertificate: sale.cancellationCertificateUrl !== '',
    isCertificateValidated: sale.cancellationCertificateIsValid
  })
  if (sale.cancellationCertificateUrl === '') return 'AWAITING_DOCS'
  if (sale.cancellationCertificateUrl !== '' && !sale.cancellationCertificateIsValid) return 'IN_REVIEW'
  if (sale.cancellationCertificateUrl !== '' && sale.cancellationCertificateIsValid) return 'VALIDATED'
}

export const getBigSaleStatus = (sale: BigSale): SaleStatusType => {
  if (sale.projectKind === 'carbon_forward') return getCarbonForwardSaleStatus({
    hasCFPA: sale.carbonForwardPurchaseAgreementDocument !== '',
    isCFPAValidated: sale.isValidCarbonForwardPurchaseAgreementDocument,
    hasCertificate: sale.cancellationCertificate !== '',
    isCertificateValidated: sale.cancellationCertificateIsValid
  })
  if (sale.cancellationCertificate === '') return 'AWAITING_DOCS'
  if (sale.cancellationCertificate !== '' && !sale.cancellationCertificateIsValid) return 'IN_REVIEW'
  if (sale.cancellationCertificate !== '' && sale.cancellationCertificateIsValid) return 'VALIDATED'
}

export const getSaleImage = (sale: Sale) => sale.projectImage ? `${sale.Url}${sale.projectImage}` : null

export const getSaleBlockchainCertificate = (sale: Sale) => sale.certificateUrl ? `${sale.Url}${sale.certificateUrl}` : null

export const getBigSaleBlockchainCertificate = (sale: BigSale) => {
  return sale.certificate ? `${sale.url}${sale.certificate}` : null
}

export const getBigSaleProjectImage = (sale: BigSale) => {
  return sale.certificate ? `${sale.url}${sale.project_thumbnail}` : null
}

export const downloadSaleCsvFile = (sales: Sale[], currencyList: Currency[]) => {
  const getCode = (currencyId: number) => currencyList.find(c => c.id === currencyId)?.code
  const results = sales.map((sale) => {
    return {
      Company: getStringWithOutColomns(sale.userCompanyName),
      Project: getStringWithOutColomns(sale.projectName),
      Year: sale.is_vintage ? sale.vintageYear : '-',
      Date: getStringWithOutColomns(`${getTransformedDate(sale.created)}`),
      Unit: getProjectUnit(sale.projectKind),
      Compensated_Amount: sale.co2_amount,
      Price: sale.token_price,
      Amount: sale.amount,
      Currency: getCode(sale.currency_token_id)
    }
  })
  const csv = getCsvFromObjectArray(results)
  downloadCsv(csv, `Sales_${getActualDateFormated()}`)
}

export const getBigSaleUnit = (sale: BigSale) => {
  const unit = sale.contribution_unit
  if (!unit) return getProjectUnit(sale.projectKind)
  return getContributionUnitInUserLanguage( unit, i18n.language, sale.co2_amount)
}

export const getSaleBejalfOf = (sale: BigSale) => {
  if (sale.fulfillment_type === 'transfer') return sale.account_holder_name
  if (['whitelabel', 'api'].includes(sale.purchase_kind)) return getRepresentingData(sale)
  return sale.presentedTo?.trim()?.length === 0 ? sale.userCompanyName : sale.presentedTo
}

export const getRepresentingData = (sale: BigSale): string => {
  if (sale.representingUseCompanyName) return sale.representingCompanyName
  return `${sale.representingFirstName} ${sale.representingLastName}`
}

export const getSaleCertificateLanguage = (sale: BigSale, t: TFunction) => {
  return {
    en: t('Languages.English'),
    es: t('Languages.Spanish'),
    ko: t('Languages.Korean'),
    fr: t('Languages.French'),
    de: t('Languages.German'),
    pt: t('Languages.Portuguese'),
    it: t('Languages.Italy'),
    pl: t('Languages.Polish'),
  }[sale.certificate_language] || t('Languages.English')
}

export const getSalePrices = (sale: BigSale, code: string) => {
  return {
    transaction: getFormatedPrice(+sale.invoiceP2pBaseAmount, code),
    vat: getFormatedPrice(+sale.invoiceP2pTaxAmount, code),
    fee: getFormatedPrice((+sale.invoiceFeeSellerBaseAmount + +sale.invoiceFeeSellerTaxAmount), code)
  }
}

export const getCTFeesInvoice = (sale: BigSale) => `${sale.url}${sale.invoiceFeeSeller}`
export const getSaleVatInvoice = (sale: BigSale) => `${sale.url}${sale.invoiceP2p}`
export const getSupplierInvoice = (sale: BigSale) => `${sale.url}${sale.supplierInvoice}`
